import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Send, Wallet } from "lucide-react";
import CardSelector from "@/components/coach/CardSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * On-demand charge modal — charges a chosen saved card on the client.
 * Caller passes `userId` (parent's user_id) + an `onClose` callback.
 *
 * Shows every saved card; coach picks which one to run this charge on.
 * If no card is saved, the modal explains and disables Charge.
 */
export default function ChargeClientModal({ userId, clientName, onClose, onCharged }) {
    const [amountStr, setAmountStr] = useState("");
    const [memo, setMemo] = useState("");
    const [cardCount, setCardCount] = useState(null); // null=loading
    const [selectedPm, setSelectedPm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const charge = async () => {
        const cents = Math.round((Number(amountStr) || 0) * 100);
        if (!cents || cents < 100) {
            toast.error("Amount must be at least $1.00");
            return;
        }
        if (!memo.trim()) {
            toast.error("Add a memo so the receipt makes sense");
            return;
        }
        setSubmitting(true);
        try {
            const r = await axios.post(
                `${API}/coach/clients/${userId}/charge`,
                {
                    amount_cents: cents,
                    memo: memo.trim(),
                    ...(selectedPm && selectedPm !== "demo" ? { payment_method_id: selectedPm } : {}),
                },
                { withCredentials: true },
            );
            toast.success(`Charged $${(cents / 100).toFixed(2)}`, {
                description: `${r.data?.status} · ${memo.trim()}`,
            });
            onCharged?.(r.data);
            onClose?.();
        } catch (e) {
            toast.error("Charge failed", { description: e?.response?.data?.detail || "Unknown error" });
        } finally {
            setSubmitting(false);
        }
    };

    const hasCard = cardCount != null && cardCount > 0;

    return (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-end md:items-center justify-center p-0 md:p-6">
            <div data-testid="charge-modal" className="bg-zinc-950 border border-zinc-800 w-full md:max-w-md md:rounded-lg max-h-[92vh] flex flex-col">
                <div className="h-1 bg-brand-red shrink-0" />
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900 shrink-0">
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">Run a charge</div>
                        <div className="font-display text-2xl tracking-wider text-white truncate">{clientName || "—"}</div>
                    </div>
                    <button onClick={onClose} className="text-zinc-300 hover:text-white shrink-0" type="button">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
                    <div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 mb-1.5">
                            Card to charge
                        </div>
                        <CardSelector
                            userId={userId}
                            value={selectedPm}
                            onChange={setSelectedPm}
                            onLoaded={(list) => setCardCount(list.length)}
                        />
                        {cardCount === 0 && (
                            <p data-testid="no-card" className="text-[11px] text-amber-300 mt-1.5">
                                No card on file — send an enrollment / card-setup link first, then charge here.
                            </p>
                        )}
                    </div>

                    <label className="block">
                        <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-300">Amount</span>
                        <div className="relative mt-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                            <input
                                type="number"
                                step="0.01"
                                min="1"
                                inputMode="decimal"
                                placeholder="0.00"
                                value={amountStr}
                                data-testid="charge-amount"
                                onChange={(e) => setAmountStr(e.target.value)}
                                className="w-full bg-black border border-zinc-800 pl-7 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand-red"
                            />
                        </div>
                    </label>

                    <label className="block">
                        <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-300">Memo</span>
                        <input
                            type="text"
                            maxLength={300}
                            placeholder='e.g. "3 extra sessions, Feb 10-14"'
                            value={memo}
                            data-testid="charge-memo"
                            onChange={(e) => setMemo(e.target.value)}
                            className="w-full bg-black border border-zinc-800 px-3 py-2.5 text-sm text-white mt-1 focus:outline-none focus:border-brand-red"
                        />
                        <span className="text-[10px] text-zinc-500 mt-1 block">Shows on the receipt and in your Revenue log.</span>
                    </label>
                </div>

                <div className="px-6 py-4 border-t border-zinc-900 flex items-center justify-end gap-2 shrink-0 bg-zinc-950">
                    <button onClick={onClose} className="btn-ghost-tactical" type="button" data-testid="charge-cancel">
                        Cancel
                    </button>
                    <button
                        onClick={charge}
                        type="button"
                        disabled={submitting || !hasCard}
                        data-testid="charge-submit"
                        className="text-[11px] tracking-[0.15em] uppercase px-4 py-2 font-semibold bg-brand-red text-white inline-flex items-center gap-1.5 disabled:opacity-40"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Charge {amountStr && hasCard && !Number.isNaN(Number(amountStr)) ? `$${Number(amountStr).toFixed(2)}` : ""}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Compact "Pack balance" widget — shows total remaining sessions + each
 * active pack credit. Tap any pack to see its usage history (TODO Phase 3).
 */
export function PackBalancesWidget({ userId }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/clients/${userId}/credits`, { withCredentials: true });
                setData(r.data);
            } catch (_e) { /* hide on err */ }
        })();
    }, [userId]);

    if (!data || data.active_count === 0) return null;
    const active = data.credits.filter((c) => c.status === "active");

    return (
        <div data-testid="pack-balances" className="p-6 border-b border-zinc-900">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-3 flex items-center gap-2">
                <Wallet className="w-3 h-3" /> Pack balance
            </div>
            <div className="flex items-baseline gap-2 mb-3">
                <span data-testid="pack-total-remaining" className="font-display text-4xl tracking-wider text-brand-red">{data.total_remaining}</span>
                <span className="text-xs tracking-[0.2em] uppercase text-zinc-300">sessions remaining</span>
            </div>
            <ul className="space-y-1.5">
                {active.map((c) => (
                    <li key={c.credit_id} className="flex items-center justify-between gap-2 text-xs px-3 py-2 border border-zinc-900 bg-black/40">
                        <span className="text-white truncate">{c.pack_name || "Session Pack"}</span>
                        <span className="text-zinc-300 whitespace-nowrap">{c.remaining_sessions}/{c.total_sessions} left</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
